import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { withTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
const FormField = (props) => {
    const { t } = props;
    const [show, setShow] = useState(false);
    const [labelUp, setLabelUp] = useState(false);
    useEffect(() => {
        if ((typeof props.htmlValue === 'string' && props.htmlValue.length > 0) || props.type === 'date' || props.placeholder) {
            setLabelUp(true);
        }
    }, [props.htmlValue, props.type, props.placeholder]);
    const calculatedId = useMemo(() => {
        return props.idSuffix ? `${props.name}_${props.idSuffix}` : props.name;
    }, [props.name, props.idSuffix]);
    const ariaDescribedBy = (helperText, invalid, name) => {
        if (helperText && invalid) {
            return name + '-helpertext' + ' ' + name + '-error';
        }
        else if (helperText && !invalid) {
            return name + '-helpertext';
        }
        else if (!helperText && invalid) {
            return name + '-error';
        }
        else {
            return null;
        }
    };
    function hasTemporaryError(frp) {
        return !!(frp.meta.error && frp.meta.touched);
    }
    const field = (input, meta, invalid) => {
        // ---- TEXTAREA ----
        if (props.type === 'textarea') {
            return (React.createElement(React.Fragment, null,
                React.createElement("textarea", Object.assign({ rows: 4 }, input, { id: calculatedId, disabled: props.disabled, readOnly: props.readonly, className: classNames('form--control', props.className, { 'disable-resize': props.type === 'textarea' && props.noResize }, { 'label-up': labelUp }), "aria-required": props.required, "aria-invalid": invalid, placeholder: props.placeholder, maxLength: props.maxLength, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined, onChange: (e) => {
                        input.onChange(e);
                        setLabelUp(e.currentTarget.value !== '');
                        if (props.onChange) {
                            props.onChange(e);
                        }
                    }, onFocus: (e) => {
                        input.onFocus(e);
                        if (props.onFocus) {
                            props.onFocus(e);
                        }
                    }, onBlur: (e) => {
                        input.onBlur(e);
                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                    } })),
                label()));
        }
        // ---- SELECT ----
        if (props.type === 'select') {
            return (React.createElement("div", { className: "form-select" },
                React.createElement("select", Object.assign({}, input, { id: calculatedId, disabled: props.disabled, className: classNames('form--control', props.className), autoComplete: props.autocomplete, "aria-required": props.required, required: props.required, "aria-invalid": invalid, onChange: (e) => {
                        input.onChange(e);
                        if (props.onChange) {
                            props.onChange(e);
                        }
                    }, onBlur: (e) => {
                        input.onBlur(e);
                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                    }, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined }), (props.options || []).map((o) => (React.createElement("option", { key: o.value, value: o.value || '', disabled: o.disabled }, o.label)))),
                label()));
        }
        // ---- CHECKBOX ----
        if (props.type === 'checkbox') {
            return (React.createElement("input", Object.assign({}, input, { id: calculatedId, disabled: props.disabled, readOnly: props.readonly, onChange: (e) => {
                    input.onChange(e);
                    if (props.onChange) {
                        props.onChange(e);
                    }
                }, onBlur: (e) => {
                    input.onBlur(e);
                    if (props.onBlur) {
                        props.onBlur(e);
                    }
                }, className: classNames('form-checkbox', props.className), type: props.type, "aria-required": props.required, "aria-invalid": invalid, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined })));
        }
        // ---- PASSWORD with show/hide ----
        if (props.type === 'password' && props.showPassword) {
            return (React.createElement("div", null,
                React.createElement("input", Object.assign({}, input, { id: calculatedId, className: classNames('form--control', { 'label-up': labelUp }), disabled: props.disabled, readOnly: props.readonly, maxLength: props.maxLength, type: show ? 'text' : props.type, "aria-required": props.required, "aria-invalid": invalid, autoComplete: props.autocomplete, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined, onChange: (e) => {
                        input.onChange(e);
                        setLabelUp(e.currentTarget.value !== '');
                        if (props.onChange) {
                            props.onChange(e);
                        }
                    }, onFocus: (e) => {
                        input.onFocus(e);
                        if (props.onFocus) {
                            props.onFocus(e);
                        }
                    }, onBlur: (e) => {
                        input.onBlur(e);
                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                    } })),
                label(),
                props.validTick && React.createElement("span", { className: "valid-tick" }),
                React.createElement("button", { type: "button", className: 'show-password' + (props.validTick ? ' has-tick' : ''), onClick: () => setShow(!show) },
                    React.createElement("svg", { className: `icon--${show ? 'eye-close' : 'eye'} icon--m` },
                        React.createElement("use", { href: `#sprite-${show ? 'eye-close' : 'eye'}` })))));
        }
        // ---- DATE ----
        if (props.type === 'date') {
            return (React.createElement("div", { className: classNames('form-date') },
                React.createElement("input", Object.assign({}, input, { id: calculatedId, disabled: props.disabled, readOnly: props.readonly, maxLength: props.maxLength, type: "date", autoComplete: props.autocomplete, "aria-required": props.required, min: props.min, max: props.max, "aria-invalid": invalid, placeholder: props.placeholder, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined, onKeyDown: props.onKeyDown, onInput: props.onInput, onChange: (e) => {
                        input.onChange(e);
                        setLabelUp(e.currentTarget.value !== '');
                        if (props.onChange) {
                            props.onChange(e);
                        }
                    }, onFocus: (e) => {
                        input.onFocus(e);
                        if (props.onFocus) {
                            props.onFocus(e);
                        }
                    }, onBlur: (e) => {
                        input.onBlur(e);
                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                    }, "aria-autocomplete": props.ariaAutocomplete, "aria-controls": props.ariaControls, "aria-activedescendant": props.ariaActivedescendant, className: classNames('form--control', { 'label-up': true }) })),
                label()));
        }
        // ---- VAT ----
        if (props.type === 'vat') {
            return (React.createElement(React.Fragment, null,
                props.countryCode && React.createElement("div", { className: "vat-enabled" }),
                React.createElement("input", Object.assign({}, input, { id: calculatedId, disabled: props.disabled, readOnly: props.readonly, maxLength: props.maxLength, type: props.type, autoComplete: props.autocomplete, "aria-required": props.required, "aria-invalid": invalid, placeholder: props.placeholder, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined, onKeyDown: props.onKeyDown, onInput: props.onInput, onChange: (e) => {
                        input.onChange(e);
                        setLabelUp(e.currentTarget.value !== '');
                        if (props.onChange) {
                            props.onChange(e);
                        }
                    }, onFocus: (e) => {
                        input.onFocus(e);
                        if (props.onFocus) {
                            props.onFocus(e);
                        }
                    }, onBlur: (e) => {
                        input.onBlur(e);
                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                    }, role: props.role, "aria-expanded": props.ariaExpanded, "aria-autocomplete": props.ariaAutocomplete, "aria-controls": props.ariaControls, "aria-activedescendant": props.ariaActivedescendant, className: classNames('form--control', { 'label-up': labelUp }), autoFocus: props.autoFocus })),
                label(),
                props.countryCode && React.createElement("p", { className: "vat-country-code" }, props.countryCode)));
        }
        // ---- TIPO “DEFAULT” (text, email, number, etc.) ----
        return (React.createElement(React.Fragment, null,
            React.createElement("input", Object.assign({}, input, { id: calculatedId, disabled: props.disabled, readOnly: props.readonly, maxLength: props.maxLength, type: props.type, autoComplete: props.autocomplete, "aria-required": props.type !== 'hidden' && props.required, "aria-invalid": invalid, placeholder: props.placeholder, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, calculatedId) || undefined, onKeyDown: props.onKeyDown, onInput: props.onInput, onChange: (e) => {
                    input.onChange(e);
                    setLabelUp(e.currentTarget.value !== '');
                    if (props.onChange) {
                        props.onChange(e);
                    }
                }, onFocus: (e) => {
                    input.onFocus(e);
                    if (props.onFocus) {
                        props.onFocus(e);
                    }
                }, onBlur: (e) => {
                    input.onBlur(e);
                    if (props.onBlur) {
                        props.onBlur(e);
                    }
                }, role: props.role, "aria-expanded": props.ariaExpanded, "aria-autocomplete": props.ariaAutocomplete, "aria-controls": props.ariaControls, "aria-activedescendant": props.ariaActivedescendant, className: classNames('form--control', { 'label-up': labelUp }, props.className), autoFocus: props.autoFocus })),
            label()));
    };
    const tooltip = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Tippy, { content: props.tooltip, placement: 'bottom', theme: 'light', interactive: true, appendTo: document.body, popperOptions: { strategy: 'fixed' } },
                React.createElement("span", { className: 'tooltip-icon tooltip-icon-ok' },
                    React.createElement("svg", { className: 'icon--info icon--s no-stroke' },
                        React.createElement("use", { href: '#sprite-info' })))),
            React.createElement(Tippy, { content: props.tooltip, placement: 'bottom', theme: 'light', interactive: true, appendTo: document.body, popperOptions: { strategy: 'fixed' } },
                React.createElement("span", { className: 'tooltip-icon tooltip-icon-error' },
                    React.createElement("svg", { className: 'icon--info-error icon--s no-stroke' },
                        React.createElement("use", { href: '#sprite-info-error' }))))));
    };
    const label = () => {
        return (React.createElement("label", { id: `${calculatedId}-label`, className: classNames('form-group-label', { 'visually-hidden': props.hiddenLabel, 'label-tooltip': props.tooltip }, props.labelClassName), htmlFor: calculatedId },
            props.label,
            props.required && React.createElement("span", { "aria-hidden": "true" }, "*"),
            props.required && props.doublerequired && React.createElement("span", { "aria-hidden": "true" }, "*"),
            props.tooltip && tooltip()));
    };
    const labelCheckbox = () => {
        let finalLabel = (props.required && !props.ispolicy ? '<span style="margin-right:7px" aria-hidden="true">(*)</span>' : '') + props.label;
        if (props.required && props.mandatoryLabelEndPosition) {
            finalLabel = props.label + ' (*)';
        }
        return (React.createElement("label", { id: `${calculatedId}-label`, className: classNames('form-group-label', 'multiline', { 'visually-hidden': props.hiddenLabel }, props.labelClassName), htmlFor: calculatedId, dangerouslySetInnerHTML: { __html: finalLabel } }));
    };
    const fieldBlock = (frp) => {
        if (props.type === 'checkbox') {
            return (React.createElement("div", { className: classNames('checkbox', { 'is-invalid': (frp.meta.error && frp.meta.touched) || frp.meta.submitError }) },
                React.createElement("div", { className: "form--control--checkbox" },
                    field(frp.input, frp.meta, (frp.meta.error && frp.meta.touched) || frp.meta.submitError),
                    labelCheckbox()),
                (frp.meta.error && frp.meta.touched && (React.createElement("div", { id: calculatedId + '-error', className: "invalid--feedback" }, frp.meta.error))) ||
                    (frp.meta.submitError && (React.createElement("div", { id: calculatedId + '-error', className: "invalid--feedback" }, frp.meta.submitError))),
                props.helperText ? (React.createElement("span", { id: calculatedId + '-helpertext', className: "form-group__helper-text" }, props.helperText)) : null));
        }
        return (React.createElement("div", { className: classNames('form-group', { 'is-invalid': (frp.meta.error && frp.meta.touched) || frp.meta.submitError }, { 'is-valid': !frp.meta.error && frp.meta.touched && frp.input.value !== '' }) },
            field(frp.input, frp.meta, hasTemporaryError(frp) || frp.meta.submitError),
            (hasTemporaryError(frp) && !(props.type === 'password' && props.helperText) && (React.createElement("div", { id: calculatedId + '-error', className: "invalid--feedback" }, frp.meta.error))) ||
                (frp.meta.submitError && (React.createElement("div", { id: calculatedId + '-error', className: "invalid--feedback" }, frp.meta.submitError))),
            props.helperText ? (React.createElement("span", { id: calculatedId + '-helpertext', className: "form-group__helper-text" }, props.helperText)) : null,
            props.type !== 'radio' &&
                props.type !== 'radiobox' &&
                props.type !== 'select' &&
                props.type !== 'textarea' && (React.createElement("span", { className: "invalid-icon", "aria-hidden": "true" },
                React.createElement("svg", { className: `icon--error-fill icon--s no-stroke` },
                    React.createElement("use", { href: `#sprite-error-fill` }))))));
    };
    if (props.type === 'radio') {
        return (React.createElement("div", { className: classNames('radio-wrapper', props.className) },
            props.label && (React.createElement("legend", { className: classNames('form-group-label', { 'visually-hidden': props.hiddenLabel }), dangerouslySetInnerHTML: { __html: props.label } })),
            (props.options || []).map((o, i) => (React.createElement("div", { key: i, className: classNames('form--control--radio', props.groupClassName) },
                React.createElement(Field, { key: i, name: props.name, type: props.type, value: o.value }, ({ input, meta }) => {
                    const invalid = !!(meta.error && meta.touched) || !!meta.submitError;
                    return (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: classNames('radio', { selected: input.checked }) },
                            React.createElement("input", { name: input.name, id: `${input.name}_${i}`, value: o.value, checked: input.checked, onChange: (e) => {
                                    input.onChange(e);
                                    if (props.onChange) {
                                        props.onChange(e);
                                    }
                                }, onBlur: (e) => {
                                    input.onBlur(e);
                                    if (props.onBlur) {
                                        props.onBlur(e);
                                    }
                                }, type: props.type, disabled: o.disabled || props.disabled, readOnly: props.readonly, "aria-invalid": invalid, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, props.name) || undefined }),
                            React.createElement("label", { htmlFor: `${props.name}_${i}`, className: "form-group-label" }, o.label),
                            input.checked && !!o.content && o.content),
                        props.helperText && (React.createElement("span", { id: props.name + '-helpertext', className: "form-group__helper-text" }, props.helperText)),
                        i === props.options.length - 1 &&
                            ((meta.error && meta.touched && (React.createElement("span", { id: input.name + '-error', className: "invalid--feedback" }, meta.error))) ||
                                (meta.submitError && (React.createElement("span", { id: input.name + '-error', className: "invalid--feedback" }, meta.submitError))))));
                }))))));
    }
    if (props.type === 'radiobox') {
        return (React.createElement("div", { className: "fieldset--plain fieldset--radiobox radiobox2" },
            props.label && (React.createElement("legend", { className: classNames('form-group__legend', { 'visually-hidden': props.hiddenLabel }), dangerouslySetInnerHTML: { __html: props.label } })),
            (props.options || []).map((o, i) => (React.createElement(Field, { key: i, name: props.name, type: props.type, value: o.value }, ({ input, meta }) => {
                const invalid = !!(meta.error && meta.touched) || !!meta.submitError;
                return (React.createElement("div", { className: "radiobox2__option" },
                    React.createElement("input", { name: input.name, id: `${input.name}_${i}`, value: o.value, defaultChecked: o.checked ? o.checked : !props.options.find((op) => op.checked) && i === 0, checked: input.checked, onChange: (e) => {
                            input.onChange(e);
                            if (props.onChange) {
                                props.onChange(e);
                            }
                        }, onBlur: (e) => {
                            input.onBlur(e);
                            if (props.onBlur) {
                                props.onBlur(e);
                            }
                        }, type: "radio", disabled: o.disabled || props.disabled, readOnly: props.readonly, "aria-describedby": ariaDescribedBy(props.helperText || '', invalid, props.name) || undefined }),
                    React.createElement("label", { htmlFor: `${props.name}_${i}` }, o.label)));
            })))));
    }
    // Caso “default” – fallback al Field
    return React.createElement(Field, { name: props.name, type: props.type }, (frp) => fieldBlock(frp));
};
FormField.defaultProps = {
    type: 'text',
};
export default withTranslation()(FormField);
